/**
 * MKS-162 — TaskDetailSlideOver Links section.
 *
 * Lists a task's links (parent/blocks/duplicates/relates), lets a project
 * member add a new one via a kind picker + a debounced task search, and
 * remove any existing one. External audience renders NOTHING — this
 * component and its parent mount on the external portal, and links must
 * never surface internal tickets to an external actor; the backend rejects
 * external tokens on all three routes anyway (T-kvv-02), so this is the UI
 * half of a two-sided rule, not the only guard.
 *
 * Navigation: rows render as a plain `<a href="/tasks/{id}">` — a router
 * `Link` or `useNavigate` is deliberately NOT used, because this component
 * tree mounts in specs without a Router (mirrors the reason the slide-over's
 * own full-page affordance is a callback prop rather than a `<Link>`). A
 * full document load when jumping to another ticket is the accepted
 * tradeoff for zero prop-drilling across the three mount points this
 * section is expected to serve (slide-over, full-page ticket view, my-tasks
 * panel).
 */
import { useEffect, useRef, useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useTaskLinks,
  useCreateTaskLink,
  useDeleteTaskLink,
} from '../../hooks/useTaskLinks';
import {
  TASK_LINK_KINDS,
  searchLinkTargets,
  type TaskLinkKind,
  type TaskLinkRecord,
  type LinkTargetCandidate,
} from '../../services/api/taskLinks';
import { useQuery } from '@tanstack/react-query';

interface TaskLinksSectionProps {
  taskId: number;
  projectId: number;
  audience?: 'internal' | 'external';
}

/** Direction-aware human label — see file header + plan for the mapping. */
function kindLabel(kind: TaskLinkKind, direction: 'outgoing' | 'incoming'): string {
  switch (kind) {
    case 'parent':
      return direction === 'outgoing' ? 'Parent' : 'Child';
    case 'blocks':
      return direction === 'outgoing' ? 'Blocks' : 'Blocked by';
    case 'duplicates':
      return 'Duplicates';
    case 'relates':
      return 'Relates to';
    default:
      return kind;
  }
}

// Tiny inline debounce — matches the pattern in TaskWatchersSection.tsx.
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function TaskLinksSection({ taskId, projectId, audience = 'internal' }: TaskLinksSectionProps) {
  const isExternal = audience === 'external';

  // Hooks called unconditionally regardless of audience (stable hook order)
  // — the external early-return below happens AFTER every hook call.
  const { data, isLoading } = useTaskLinks(taskId);
  const createLink = useCreateTaskLink();
  const deleteLink = useDeleteTaskLink();

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState<TaskLinkKind>('relates');
  const [pickerQuery, setPickerQuery] = useState('');
  const debouncedQuery = useDebounced(pickerQuery, 150);
  const pickerRef = useRef<HTMLDivElement>(null);

  const candidatesQuery = useQuery<LinkTargetCandidate[]>({
    queryKey: ['task-link-targets', projectId, debouncedQuery],
    queryFn: () => searchLinkTargets(projectId, debouncedQuery),
    enabled: !isExternal && isPickerOpen && debouncedQuery.trim().length >= 2,
  });

  useEffect(() => {
    if (isExternal) return;
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExternal]);

  if (isExternal) {
    return null;
  }

  const links: TaskLinkRecord[] = data ?? [];
  const linkedTaskIds = new Set(links.map((l) => l.task.id));
  const candidates = (candidatesQuery.data ?? []).filter(
    (c) => c.id !== taskId && !linkedTaskIds.has(c.id),
  );

  function handleSelectCandidate(candidateId: number) {
    createLink.mutate({ taskId, toTaskId: candidateId, kind: pickerKind });
    setIsPickerOpen(false);
    setPickerQuery('');
  }

  const inlineError = createLink.error?.message ?? deleteLink.error?.message ?? null;

  return (
    <section
      aria-label="Links"
      className="pt-3 border-t border-ms-border-subtle"
      data-testid="task-links-section"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ms-text-secondary">
          Links
        </h3>
        <div className="flex items-center gap-2">
          {links.length > 0 && (
            <span className="text-xs text-ms-text-secondary tabular-nums">{links.length}</span>
          )}
          <div ref={pickerRef} className="relative">
            <button
              type="button"
              onClick={() => setIsPickerOpen((v) => !v)}
              disabled={createLink.isPending}
              aria-label="Add link"
              data-testid="task-links-add-trigger"
              className="inline-flex items-center gap-1 rounded-full border border-ms-border bg-ms-surface px-2 py-1 text-xs font-medium text-ms-text hover:bg-ms-canvas disabled:opacity-50"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add link
            </button>
            {isPickerOpen && (
              <div className="absolute right-0 z-10 mt-1 w-72 rounded-lg border border-ms-border bg-ms-surface shadow-lg">
                <select
                  value={pickerKind}
                  onChange={(e) => setPickerKind(e.target.value as TaskLinkKind)}
                  data-testid="task-links-kind-select"
                  className="w-full border-b border-ms-border-subtle px-2 py-1.5 text-sm focus:outline-none"
                >
                  {TASK_LINK_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {kindLabel(kind, 'outgoing')}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  autoFocus
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Search tickets…"
                  data-testid="task-links-search-input"
                  className="w-full rounded-t-none border-b border-ms-border-subtle px-2 py-1.5 text-sm focus:outline-none"
                />
                <div className="max-h-48 overflow-auto">
                  {candidatesQuery.isLoading ? (
                    <p className="px-3 py-2 text-xs text-ms-text-muted italic">Searching…</p>
                  ) : candidates.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-ms-text-muted italic">No matches.</p>
                  ) : (
                    candidates.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleSelectCandidate(c.id)}
                        disabled={createLink.isPending}
                        data-testid={`task-links-candidate-${c.id}`}
                        className="block w-full px-3 py-2 text-left text-sm text-ms-text hover:bg-ms-canvas disabled:opacity-50"
                      >
                        <span className="font-medium">{c.code}</span> {c.name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {inlineError && (
        <p className="mb-2 text-xs text-red-600" data-testid="task-links-error">
          {inlineError}
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-ms-text-muted italic">Loading links…</p>
      ) : links.length === 0 ? (
        <p className="text-sm text-ms-text-muted italic">No links yet.</p>
      ) : (
        <ul className="space-y-1" data-testid="task-links-list">
          {links.map((link) => (
            <li
              key={link.linkId}
              className="flex items-center justify-between gap-2 rounded-md bg-ms-canvas px-2 py-1.5 text-sm"
              data-testid={`task-link-${link.linkId}`}
            >
              <a
                href={`/tasks/${link.task.id}`}
                className="flex min-w-0 items-center gap-2 text-ms-text hover:underline"
              >
                <span className="shrink-0 rounded-full bg-ms-surface border border-ms-border-subtle px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ms-text-secondary">
                  {kindLabel(link.kind, link.direction)}
                </span>
                <span className="shrink-0 font-medium">{link.task.code}</span>
                <span className="truncate text-ms-text-secondary">{link.task.name}</span>
                {link.task.stateName && (
                  <span
                    className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white"
                    style={{ backgroundColor: link.task.stateColor ?? '#64748b' }}
                  >
                    {link.task.stateName}
                  </span>
                )}
              </a>
              <button
                type="button"
                onClick={() => deleteLink.mutate({ taskId, linkId: link.linkId })}
                disabled={deleteLink.isPending}
                aria-label={`Remove link to ${link.task.code}`}
                data-testid={`task-link-remove-${link.linkId}`}
                className="shrink-0 text-ms-text-muted hover:text-ms-text disabled:opacity-50"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
